import z from "zod/v4";
import type { Database } from "../../../../__definitions__/database-definitions";
import type { UUID } from "../../../../utils/uuid";

type DBSegment = Database["public"]["Tables"]["segment"]["Row"];

export type Segment = Pick<
  DBSegment,
  "id" | "name" | "description" | "is_default" | "is_recipient_type" | "operation"
> & {
  segment_json: NonNullable<Database["public"]["Tables"]["segment"]["Row"]["segment_json"]>;
};

type SegmentJson = NonNullable<Segment["segment_json"]>;

type SegmentGroup = SegmentJson["segments"][number];

export type Condition = SegmentGroup["conditions"][number];

const baseCondition = {
  field: z.string().min(1, { message: "Field is required" }),
  invert: z.boolean(),
};

// One branch for "IS EMPTY"
const isEmptySchema = z.object({
  ...baseCondition,
  operation: z.literal("IS EMPTY"),
  values: z.array(z.string()).length(0, { message: "Must be empty" }),
});

// One branch for all other operations
const otherOpsSchema = z.object({
  ...baseCondition,
  operation: z.enum(["IS", "ON", "BEFORE", "AFTER"]),
  values: z.array(z.string()).min(1, { message: "Must have at least one value" }),
});

// Combine into discriminated union
const conditionSchema: z.ZodType<Condition> = z.discriminatedUnion("operation", [
  isEmptySchema,
  otherOpsSchema,
]);

const makeSegmentGroupSchema = (recipientTypeRequired: boolean): z.ZodType<SegmentGroup> =>
  z.lazy(() =>
    z.object({
      type: z.literal("segment_group"),
      recipient_type_key: recipientTypeRequired
        ? z.string().min(1, { message: "Audience is required" })
        : z.string().nullish(), // <-- make optional when needed
      operation: z.enum(["AND", "OR"]),
      conditions: z.array(conditionSchema),
    }),
  );

const segmentGroupSchema = makeSegmentGroupSchema(true);

const makeSegmentJsonSchema = (groupSchema: z.ZodType<SegmentGroup>): z.ZodType<SegmentJson> =>
  z.lazy(() =>
    z.object({
      type: z.literal("segment"),
      operation: z.enum(["AND", "OR"]),
      segments: z.array(groupSchema),
    }),
  );

const segmentJsonSchema = makeSegmentJsonSchema(segmentGroupSchema);

const baseSegmentSchema = z.object({
  id: z.string().transform((x) => x as UUID),
  name: z.string().min(1, { message: "Name is required" }),
  description: z.string(),
  operation: z.enum(["AND", "OR"]),
  is_default: z.boolean().default(false),
});

export const segmentSchema: z.ZodType<Segment> = baseSegmentSchema.extend({
  is_recipient_type: z.literal(false),
  segment_json: segmentJsonSchema,
});

export const segmentSchemaNonRequiredName: z.ZodType<Segment> = baseSegmentSchema.extend({
  name: z.string().nullable().default(""),
  is_recipient_type: z.literal(false),
  segment_json: segmentJsonSchema,
});

const recipientTypeGroupSchema = makeSegmentGroupSchema(false);
const recipientTypeJsonSchema = makeSegmentJsonSchema(recipientTypeGroupSchema);
export const recipientTypeSchema: z.ZodType<Segment> = baseSegmentSchema.extend({
  is_recipient_type: z.literal(true),
  segment_json: recipientTypeJsonSchema,
});

export const recipientTypeSchemaNonRequiredName: z.ZodType<Segment> = baseSegmentSchema.extend({
  name: z.string().nullable().default(""),
  is_recipient_type: z.literal(true),
  segment_json: recipientTypeJsonSchema,
});

export type SegmentMode = "AUDIENCE" | "SEGMENT";

export type GetPersonsInSegmentJsonPerson =
  Database["public"]["Functions"]["get_persons_in_segment_json"]["Returns"][number];
